/* ============================================================
   ET - COLLECTION CONTROLS — filter drawer + pill scroller.

   Two jobs, both small on purpose:

   1. The filter drawer is a modal dialog: open, focus trap, Escape, focus restore, scroll lock.
      It does NOT filter anything itself. The drawer contains a plain GET <form> pointing at the
      collection URL, so pressing "Apply" is an ordinary navigation and SHOPIFY does the filtering,
      the pagination and the counting. The live theme's controller hid cards client-side, which
      short-pages a paginated collection (24 rendered, 12 shown) and reports the page-visible number
      as the collection total — see the header comment in sections/et-collection-grid.liquid.

   2. Scroll the selected pill into view, because the pill row is a horizontal scroller at every
      width and the selected pill is frequently off-screen on a phone.

   Nothing here hides a product card, and nothing here rewrites a count. With JavaScript off the
   grid still renders every product, the pills still navigate (they are <a> elements) and only the
   drawer is unavailable — which is why the section hides the Filters button inside <noscript>.

   Impact's `.lock` scroll lock is deliberately NOT reused: it is driven by a private static
   ref-counter inside DialogElement, so adding or removing that class by hand desynchronises the
   counter and the next cart-drawer close would strip our lock. Same reasoning, and the same fix,
   as assets/et-header.js.
   ============================================================ */

(function () {
  'use strict';

  if (window.__etCollectionControlsInit) return;
  window.__etCollectionControlsInit = true;

  var SCROLL_LOCK_CLASS = 'et-cc-scroll-lock';
  var TRANSITION_MS = 240; /* keep in sync with the .et-cc-drawer transitions in et-collection-controls.css */

  var FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]),' +
    ' textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  // ----------------------------------------------------------------
  // Helpers
  // ----------------------------------------------------------------

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function focusables(container) {
    return Array.prototype.filter.call(container.querySelectorAll(FOCUSABLE), function (el) {
      return el.offsetWidth > 0 || el.offsetHeight > 0 || el === document.activeElement;
    });
  }

  var lockCount = 0;

  function lockScroll() {
    lockCount += 1;
    document.documentElement.classList.add(SCROLL_LOCK_CLASS);
  }

  function unlockScroll() {
    lockCount = Math.max(0, lockCount - 1);
    if (lockCount === 0) document.documentElement.classList.remove(SCROLL_LOCK_CLASS);
  }

  /* Attribute selector rather than '#' + id: section ids are safe, but this keeps the lookup working if the id
     ever picks up a character querySelector would choke on. */
  function byId(scope, id) {
    return id ? scope.querySelector('[id="' + String(id).replace(/"/g, '\\"') + '"]') : null;
  }

  // ----------------------------------------------------------------
  // Drawer
  // ----------------------------------------------------------------

  function isOpen(drawer) {
    return drawer.classList.contains('is-open');
  }

  function openDrawer(root, trigger) {
    var drawer = byId(root, trigger.getAttribute('aria-controls'));
    if (!drawer || isOpen(drawer)) return;

    drawer.etTrigger = trigger;
    window.clearTimeout(drawer.etHideTimer);

    drawer.hidden = false;
    /* Forced reflow between the two steps, or the browser coalesces "un-hide" and "is-open" into one frame and
       the panel jumps in without transitioning. */
    void drawer.offsetHeight;
    drawer.classList.add('is-open');
    trigger.setAttribute('aria-expanded', 'true');
    lockScroll();

    /* The dialog itself takes focus, not its first control. Focusing the close button is what put a focus ring
       around the X the moment the drawer opened — Chrome treats a programmatic focus that follows a click as
       focus-visible — and the design has no ring there. The tab trap below still keeps the keyboard inside. */
    var panel = drawer.querySelector('.et-cc-drawer__panel');
    if (panel) {
      panel.setAttribute('tabindex', '-1');
      panel.focus();
    }
  }

  function closeDrawer(drawer, restoreFocus) {
    if (!isOpen(drawer)) return;

    drawer.classList.remove('is-open');
    unlockScroll();

    var trigger = drawer.etTrigger;
    if (trigger) trigger.setAttribute('aria-expanded', 'false');

    var hide = function () {
      drawer.hidden = true;
    };

    if (prefersReducedMotion()) {
      hide();
    } else {
      drawer.etHideTimer = window.setTimeout(hide, TRANSITION_MS);
    }

    /* Focus goes back to the control that opened the drawer, before it is hidden — moving focus into a hidden
       subtree is what makes screen readers land on <body>. */
    if (restoreFocus && trigger && document.contains(trigger)) trigger.focus();
    drawer.etTrigger = null;
  }

  function trapTab(drawer, event) {
    var panel = drawer.querySelector('.et-cc-drawer__panel');
    if (!panel) return;

    var items = focusables(panel);
    if (!items.length) {
      event.preventDefault();
      return;
    }

    var first = items[0];
    var last = items[items.length - 1];
    var active = document.activeElement;

    if (event.shiftKey && (active === first || !panel.contains(active))) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  }

  // ----------------------------------------------------------------
  // Pill scroller
  // ----------------------------------------------------------------

  /* `auto`, never `smooth`: this runs at init and the row must not animate on every page load. */
  function revealSelectedPill(root) {
    var scroller = root.querySelector('[data-et-cc-scroller]');
    if (!scroller) return;

    var selected = scroller.querySelector('.et-collection-controls__pill.is-selected');
    if (!selected) return;

    var overflow = scroller.scrollWidth - scroller.clientWidth;
    if (overflow <= 1) return;

    var target = selected.offsetLeft - (scroller.clientWidth - selected.offsetWidth) / 2;
    scroller.scrollLeft = Math.max(0, Math.min(target, overflow));
  }

  // ----------------------------------------------------------------
  // Filtering — serie pills + the facet drawer, one engine
  //
  // Deliberately NO navigation, no history entry, no URL change and no reload: switching between Standaard
  // and Plus, or ticking a facet, swaps the visible cards on the spot, matching the live theme. Everything
  // below therefore only ever touches classes and text; nothing here writes to location or history.
  //
  // Every card carries its own filter data (see the contract comment in snippets/et-collection-product-card
  // .liquid), so the facet lists are derived from the products actually on the page rather than from
  // collection.filters — which on this store only exposes price and availability.
  // ----------------------------------------------------------------

  var HIDDEN_CLASS = 'et-card--filtered-out';

  /* Groups the design shows expanded on open. The rest start collapsed, which is what keeps a 20-value size
     list from burying the footer. */
  var OPEN_GROUPS = ['serie', 'color'];

  /* Per-path so that Standaard on /collections/dubbel-series does not leak onto another collection.
     sessionStorage, not localStorage: a series choice is a within-visit intent, not a lasting preference.
     Wrapped because Safari private mode throws on access rather than returning null. */
  function storageKey() {
    return 'et-cc-serie:' + window.location.pathname;
  }

  function readStoredSerie() {
    try {
      return window.sessionStorage.getItem(storageKey());
    } catch (error) {
      return null;
    }
  }

  function storeSerie(value) {
    try {
      window.sessionStorage.setItem(storageKey(), value);
    } catch (error) {
      /* Private mode or blocked storage: the filter still works, it just will not survive a back-navigation. */
    }
  }

  function escapeHtml(value) {
    return String(value).replace(/[&<>"']/g, function (ch) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch];
    });
  }

  function splitValues(raw) {
    if (!raw) return [];
    return raw.split('|').map(function (part) { return part.trim(); }).filter(Boolean);
  }

  function hasAny(haystack, needles) {
    for (var i = 0; i < needles.length; i += 1) {
      if (haystack.indexOf(needles[i]) !== -1) return true;
    }
    return false;
  }

  function toggleIn(list, value, on) {
    var at = list.indexOf(value);
    if (on && at === -1) list.push(value);
    else if (!on && at !== -1) list.splice(at, 1);
  }

  // ---------- Card model ----------

  /* Read once per section. The cards never change after render — the controller only ever toggles a class on
     them — so re-reading six attributes per card on every keystroke of the price field would be pure waste. */
  function modelOf(root) {
    if (root.etCcModel) return root.etCcModel;

    root.etCcModel = Array.prototype.map.call(root.querySelectorAll('[data-et-cg-card]'), function (el) {
      return {
        el: el,
        plus: el.getAttribute('data-plus') === 'true',
        available: el.getAttribute('data-available') === 'true',
        price: parseInt(el.getAttribute('data-price'), 10) || 0, // cents
        colors: splitValues(el.getAttribute('data-colors')),
        handles: splitValues(el.getAttribute('data-handles')),
        size: (el.getAttribute('data-size') || '').trim()
      };
    });

    return root.etCcModel;
  }

  function serieOf(card) {
    return card.plus ? 'plus' : 'regular';
  }

  /* The pills are single-choice, the Serie Type checkboxes are not, so the state is the array and the pill
     row reads back from it: one serie selected names that pill, anything else (none, or both) is "all". */
  function serieKey(series) {
    return series.length === 1 ? series[0] : 'all';
  }

  function stateOf(root) {
    if (!root.etCcState) {
      var prices = modelOf(root).map(function (card) { return card.price; });
      var floor = prices.length ? Math.floor(Math.min.apply(null, prices) / 100) : 0;
      var ceil = prices.length ? Math.ceil(Math.max.apply(null, prices) / 100) : 1;
      if (!isFinite(floor)) floor = 0;
      if (!isFinite(ceil) || ceil <= floor) ceil = floor + 1;

      root.etCcState = {
        series: [],
        inStock: false,
        colors: [],
        handles: [],
        sizes: [],
        priceFloor: floor,
        priceCeil: ceil,
        priceMin: floor,
        priceMax: ceil
      };
    }

    return root.etCcState;
  }

  /* `skip` leaves one facet out of the test, which is how a facet counts its own values: "Wit (18)" means
     "18 products match everything ELSE and are white", not "18 products match, white included". */
  function matches(card, state, skip) {
    if (skip !== 'serie' && state.series.length && state.series.indexOf(serieOf(card)) === -1) return false;
    if (skip !== 'instock' && state.inStock && !card.available) return false;
    if (skip !== 'color' && state.colors.length && !hasAny(card.colors, state.colors)) return false;
    if (skip !== 'handle' && state.handles.length && !hasAny(card.handles, state.handles)) return false;
    if (skip !== 'size' && state.sizes.length && state.sizes.indexOf(card.size) === -1) return false;

    if (skip !== 'price') {
      var major = card.price / 100;
      if (major < state.priceMin || major > state.priceMax) return false;
    }

    return true;
  }

  function countFor(root, state, skip, predicate) {
    var total = 0;
    modelOf(root).forEach(function (card) {
      if (matches(card, state, skip) && predicate(card)) total += 1;
    });
    return total;
  }

  /* Distinct values in the order the cards introduce them, so the list matches the grid's reading order
     rather than an alphabetical one nobody asked for. */
  function distinct(root, getter) {
    var seen = {};
    var out = [];

    modelOf(root).forEach(function (card) {
      var values = getter(card);
      if (typeof values === 'string') values = values ? [values] : [];
      values.forEach(function (value) {
        if (value && !seen[value]) {
          seen[value] = true;
          out.push(value);
        }
      });
    });

    return out;
  }

  // ---------- Facet UI ----------

  function checkIcon() {
    return (
      '<svg class="et-cc-facet__tick" viewBox="0 0 24 24" width="14" height="14" fill="none" aria-hidden="true">' +
      '<path d="M5 12l5 5 9-10" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg>'
    );
  }

  function chevron() {
    return (
      '<svg class="et-cc-facet__chevron" viewBox="0 0 24 24" width="20" height="20" fill="none" aria-hidden="true">' +
      '<path d="M6 9l6 6 6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>'
    );
  }

  function optionRow(facet, value, text, showCount) {
    return (
      '<label class="et-cc-facet__option">' +
        '<input type="checkbox" class="et-cc-facet__input" data-et-cc-facet="' + escapeHtml(facet) + '" value="' + escapeHtml(value) + '">' +
        '<span class="et-cc-facet__box">' + checkIcon() + '</span>' +
        '<span class="et-cc-facet__name">' + escapeHtml(text) +
          (showCount ? ' <span class="et-cc-facet__count" data-et-cc-count="' + escapeHtml(facet) + '" data-et-cc-value="' + escapeHtml(value) + '"></span>' : '') +
        '</span>' +
      '</label>'
    );
  }

  function facetGroup(key, title, open, inner) {
    return (
      '<div class="et-cc-facet' + (open ? ' is-open' : '') + '" data-et-cc-group="' + escapeHtml(key) + '">' +
        '<button type="button" class="et-cc-facet__head" data-et-cc-facet-toggle aria-expanded="' + (open ? 'true' : 'false') + '">' +
          '<span class="et-cc-facet__title">' + escapeHtml(title) + '</span>' + chevron() +
        '</button>' +
        '<div class="et-cc-facet__body">' + inner + '</div>' +
      '</div>'
    );
  }

  /* The Serie Type rows are the pills restated as checkboxes, so their wording can never drift from the row
     above the grid — the merchant edits one label, not two. "Alles" is not a value: no serie ticked already
     means every serie. */
  function serieOptions(root) {
    var out = [];

    Array.prototype.forEach.call(root.querySelectorAll('[data-et-cc-serie]'), function (pill) {
      var value = pill.getAttribute('data-et-cc-serie');
      if (!value || value === 'all') return;

      var titleEl = pill.querySelector('.et-collection-controls__pill-title');
      var text = (titleEl ? titleEl.textContent : pill.textContent) || value;
      out.push({ value: value, label: text.replace(/\s+/g, ' ').trim() });
    });

    return out;
  }

  function buildFacets(root) {
    var host = root.querySelector('[data-et-cc-facets]');
    if (!host) return;

    var drawer = root.querySelector('[data-et-cc-drawer]');
    var state = stateOf(root);
    var model = modelOf(root);
    var showCounts = !drawer || drawer.getAttribute('data-et-cc-counts') !== 'false';

    function label(name) {
      return drawer ? (drawer.getAttribute('data-et-cc-label-' + name) || '').trim() : '';
    }

    var html = '';

    /* Serie only earns a group when the collection actually holds both kinds — on an all-Plus collection it
       would be a checkbox that can only ever hide everything. */
    var series = serieOptions(root);
    var hasPlus = model.some(function (card) { return card.plus; });
    var hasRegular = model.some(function (card) { return !card.plus; });

    if (label('serie') && series.length > 1 && hasPlus && hasRegular) {
      html += facetGroup('serie', label('serie'), OPEN_GROUPS.indexOf('serie') !== -1, series.map(function (opt) {
        return optionRow('serie', opt.value, opt.label, showCounts);
      }).join(''));
    }

    var colors = distinct(root, function (card) { return card.colors; });
    if (label('color') && colors.length) {
      html += facetGroup('color', label('color'), OPEN_GROUPS.indexOf('color') !== -1, colors.map(function (value) {
        return optionRow('color', value, value, showCounts);
      }).join(''));
    }

    if (label('price')) {
      var currency = (drawer && drawer.getAttribute('data-et-cc-currency')) || '';
      html += facetGroup('price', label('price'), false,
        '<div class="et-cc-price">' +
          '<div class="et-cc-price__fields">' +
            '<div class="et-cc-price__field">' +
              '<input type="number" class="et-cc-price__num" data-et-cc-price-min inputmode="numeric" value="' + state.priceFloor + '" aria-label="' + escapeHtml(label('price')) + '">' +
              '<span class="et-cc-price__cur">' + escapeHtml(currency) + '</span>' +
            '</div>' +
            '<span class="et-cc-price__dash"></span>' +
            '<div class="et-cc-price__field">' +
              '<input type="number" class="et-cc-price__num" data-et-cc-price-max inputmode="numeric" value="' + state.priceCeil + '" aria-label="' + escapeHtml(label('price')) + '">' +
              '<span class="et-cc-price__cur">' + escapeHtml(currency) + '</span>' +
            '</div>' +
          '</div>' +
          '<div class="et-cc-price__slider" data-et-cc-price-slider>' +
            '<div class="et-cc-price__track"></div>' +
            '<div class="et-cc-price__range" data-et-cc-price-fill></div>' +
            '<input type="range" class="et-cc-price__handle" data-et-cc-price-range-min min="' + state.priceFloor + '" max="' + state.priceCeil + '" value="' + state.priceFloor + '" aria-hidden="true" tabindex="-1">' +
            '<input type="range" class="et-cc-price__handle" data-et-cc-price-range-max min="' + state.priceFloor + '" max="' + state.priceCeil + '" value="' + state.priceCeil + '" aria-hidden="true" tabindex="-1">' +
          '</div>' +
        '</div>');
    }

    var sizes = distinct(root, function (card) { return card.size; });
    if (label('size') && sizes.length) {
      html += facetGroup('size', label('size'), false, sizes.map(function (value) {
        return optionRow('size', value, value, showCounts);
      }).join(''));
    }

    var handles = distinct(root, function (card) { return card.handles; });
    if (label('handle') && handles.length) {
      html += facetGroup('handle', label('handle'), false, handles.map(function (value) {
        return optionRow('handle', value, value, showCounts);
      }).join(''));
    }

    host.innerHTML = html;
  }

  // ---------- Apply ----------

  /* The count must report what the visitor can actually see, or Standaard on a mixed collection claims a
     total that includes every hidden Plus card. */
  function updateCount(root, visible) {
    Array.prototype.forEach.call(root.querySelectorAll('[data-et-cc-count-value]'), function (node) {
      node.textContent = String(visible);
      /* Only the label next to a LIVE count follows it; a paginated total stays as the server wrote it. */
      var label = node.parentNode && node.parentNode.querySelector('[data-et-cc-count-label]');
      if (label) setPlural(label, visible);
    });
  }

  function setPlural(node, n) {
    var label = n === 1 ? node.getAttribute('data-one') : node.getAttribute('data-other');
    if (label) node.textContent = label;
  }

  /* "1 product" / "Show 1 result": the labels carry both forms so the live count can pick the right one. */
  function pluralise(root, selector, n) {
    Array.prototype.forEach.call(root.querySelectorAll(selector), function (node) { setPlural(node, n); });
  }

  /* The server cannot know a client-side selection, so the badge is created here rather than rendered — the
     Filters button ships without one and gains it the moment something is ticked. Serie is excluded: the
     pill row already shows that choice, and counting it would leave the button permanently badged. */
  function updateBadge(root, state) {
    var button = root.querySelector('[data-et-cc-filters-open]');
    if (!button) return;

    var active = state.colors.length + state.handles.length + state.sizes.length +
      (state.inStock ? 1 : 0) +
      (state.priceMin > state.priceFloor || state.priceMax < state.priceCeil ? 1 : 0);

    var badge = button.querySelector('.et-collection-controls__filters-count');

    if (!active) {
      if (badge) badge.remove();
      return;
    }

    if (!badge) {
      badge = document.createElement('span');
      badge.className = 'et-collection-controls__filters-count';
      (button.querySelector('.et-collection-controls__filters-inner') || button).appendChild(badge);
    }

    badge.textContent = String(active);
  }

  function syncPills(root, state) {
    var current = serieKey(state.series);

    Array.prototype.forEach.call(root.querySelectorAll('[data-et-cc-serie]'), function (pill) {
      var on = pill.getAttribute('data-et-cc-serie') === current;
      pill.classList.toggle('is-selected', on);
      pill.setAttribute('aria-pressed', on ? 'true' : 'false');
    });

    root.setAttribute('data-et-cc-active-serie', current);
  }

  function syncInputs(root, state) {
    Array.prototype.forEach.call(root.querySelectorAll('.et-cc-facet__input'), function (input) {
      var facet = input.getAttribute('data-et-cc-facet');
      var list = facet === 'serie' ? state.series
        : facet === 'color' ? state.colors
        : facet === 'handle' ? state.handles
        : facet === 'size' ? state.sizes
        : null;
      if (list) input.checked = list.indexOf(input.value) !== -1;
    });

    Array.prototype.forEach.call(root.querySelectorAll('[data-et-cc-instock]'), function (button) {
      button.setAttribute('aria-checked', state.inStock ? 'true' : 'false');
      button.classList.toggle('is-on', state.inStock);
    });

    var span = state.priceCeil - state.priceFloor;

    Array.prototype.forEach.call(root.querySelectorAll('[data-et-cc-price-slider]'), function (slider) {
      var min = slider.querySelector('[data-et-cc-price-range-min]');
      var max = slider.querySelector('[data-et-cc-price-range-max]');
      var fill = slider.querySelector('[data-et-cc-price-fill]');

      if (min) min.value = state.priceMin;
      if (max) max.value = state.priceMax;

      if (fill && span > 0) {
        fill.style.left = ((state.priceMin - state.priceFloor) / span) * 100 + '%';
        fill.style.right = (100 - ((state.priceMax - state.priceFloor) / span) * 100) + '%';
      }
    });

    /* Never while it has focus: rewriting the value under the caret is what makes a number field impossible
       to clear and retype. */
    Array.prototype.forEach.call(root.querySelectorAll('[data-et-cc-price-min]'), function (input) {
      if (document.activeElement !== input) input.value = state.priceMin;
    });
    Array.prototype.forEach.call(root.querySelectorAll('[data-et-cc-price-max]'), function (input) {
      if (document.activeElement !== input) input.value = state.priceMax;
    });
  }

  /* The server can only place an image tile among ALL of the collection's products. The serie pills then
     hide roughly half of them - server-side on first paint, and again on every pill click - so the tile the
     visitor actually sees it at drifts away from the one the merchant set. On a mixed collection the tile
     configured as 7th was landing 4th.

     Re-placed here against the cards that are actually VISIBLE, using grid `order`. Steps of ten leave room
     to drop a block between two cards without renumbering them, and the +5 offset is what puts the block
     immediately before the card that would otherwise hold its slot. Hidden cards are skipped entirely:
     `order` has no effect on a display:none grid item, so they neither count nor take a number. */
  function placeBlocks(root) {
    var blocks = root.querySelectorAll('[data-et-cg-position]');
    if (!blocks.length) return;

    var seen = 0;

    Array.prototype.forEach.call(root.querySelectorAll('[data-et-cg-card]'), function (card) {
      if (card.classList.contains(HIDDEN_CLASS)) {
        card.style.order = '';
        return;
      }

      seen += 1;
      card.style.order = String(seen * 10);
    });

    Array.prototype.forEach.call(blocks, function (block) {
      var slot = parseInt(block.getAttribute('data-et-cg-position'), 10);

      if (!slot || slot < 1) slot = 1;
      // Past the end of what is showing means "after the last card", not "off the grid".
      if (slot > seen + 1) slot = seen + 1;

      block.style.order = String((slot - 1) * 10 + 5);
    });
  }

  function applyFilters(root) {
    var state = stateOf(root);
    var model = modelOf(root);
    var visible = 0;

    model.forEach(function (card) {
      var ok = matches(card, state, null);
      card.el.classList.toggle(HIDDEN_CLASS, !ok);
      if (ok) visible += 1;
    });

    Array.prototype.forEach.call(root.querySelectorAll('[data-et-cc-count]'), function (node) {
      var facet = node.getAttribute('data-et-cc-count');
      var value = node.getAttribute('data-et-cc-value');
      var n = 0;

      if (facet === 'serie') {
        n = countFor(root, state, 'serie', function (card) { return serieOf(card) === value; });
      } else if (facet === 'color') {
        n = countFor(root, state, 'color', function (card) { return card.colors.indexOf(value) !== -1; });
      } else if (facet === 'handle') {
        n = countFor(root, state, 'handle', function (card) { return card.handles.indexOf(value) !== -1; });
      } else if (facet === 'size') {
        n = countFor(root, state, 'size', function (card) { return card.size === value; });
      }

      node.textContent = '(' + n + ')';
    });

    Array.prototype.forEach.call(root.querySelectorAll('[data-et-cc-result-count]'), function (node) {
      node.textContent = String(visible);
    });
    pluralise(root, '[data-et-cc-result-label]', visible);

    updateCount(root, visible);
    updateBadge(root, state);
    syncPills(root, state);
    syncInputs(root, state);

    /* Only meaningful when the collection has products at all — an empty collection is already showing its
       own empty state and must not gain a second "nothing matches" message on top of it. */
    var empty = root.querySelector('[data-et-cc-serie-empty]');
    if (empty) empty.hidden = !(model.length > 0 && visible === 0);

    // After the toggling, never before: the placement is computed from what is visible NOW.
    placeBlocks(root);
  }

  function setSerie(root, serie) {
    var state = stateOf(root);
    state.series = serie && serie !== 'all' ? [serie] : [];
    applyFilters(root);
  }

  function resetFilters(root) {
    var state = stateOf(root);

    state.inStock = false;
    state.colors = [];
    state.handles = [];
    state.sizes = [];
    state.priceMin = state.priceFloor;
    state.priceMax = state.priceCeil;

    /* Back to the serie the merchant configured, not to "all": on a collection whose default is Standaard,
       clearing the filters and being handed the Plus products too is not what "remove filters" promises. */
    var controls = root.querySelector('[data-et-cc]');
    var initial = (controls && controls.getAttribute('data-et-cc-default-serie')) || 'all';
    state.series = initial && initial !== 'all' ? [initial] : [];
    storeSerie(initial);

    applyFilters(root);
  }

  function clamp(value, low, high) {
    if (isNaN(value)) return value;
    return Math.min(Math.max(value, low), high);
  }

  function initFilters(root) {
    var pills = root.querySelectorAll('[data-et-cc-serie]');
    var state = stateOf(root);

    var valid = { all: false };
    Array.prototype.forEach.call(pills, function (pill) {
      valid[pill.getAttribute('data-et-cc-serie')] = true;
    });

    if (pills.length) {
      var stored = readStoredSerie();
      /* An explicit ?serie= outranks what this session remembered - the header's series cards link here with
         ?serie=plus / ?serie=regular, so the visitor has just asked for that serie by name. */
      var requested = null;
      try { requested = new URLSearchParams(window.location.search).get('serie'); } catch (e) { requested = null; }
      /* Read off the controls element, not the section: `root` is the Shopify-generated
         <section class="shopify-section et-collection-grid"> wrapper, which Liquid cannot add attributes to. */
      var controls = root.querySelector('[data-et-cc]');
      var initial = (controls && controls.getAttribute('data-et-cc-default-serie')) || 'all';
      var effective = requested && valid[requested] ? requested : (stored && valid[stored] ? stored : initial);
      if (requested && valid[requested]) storeSerie(requested);

      /* `valid` only proves a PILL with that value exists, never that any card matches it. Liquid already refuses
         to open on a serie that matches nothing (see the default_serie fallback in et-collection-grid.liquid), but
         a value restored from sessionStorage bypasses that guard entirely: one click on "Standaard" on
         wasdroom-series — 18 products, every one of them Plus — would otherwise leave that collection showing zero
         products on every later load for the rest of the session. Apply the same rule client-side. */
      var model = modelOf(root);
      if (model.length && effective !== 'all') {
        var hits = 0;
        model.forEach(function (card) {
          if (serieOf(card) === effective) hits += 1;
        });
        if (hits === 0) effective = valid.all ? 'all' : initial;
      }

      state.series = effective && effective !== 'all' ? [effective] : [];
    }

    buildFacets(root);

    /* Always run once, even when the serie is the value the server already rendered. The cards are correct
       either way — the server hid them — but the product count is not: Liquid prints the collection total,
       which on a mixed collection is the number BEFORE the default serie hides half of it. Skipping this call
       to save a layout pass is what made dubbel-series load as "16 producten" above 8 visible cards. */
    applyFilters(root);

    root.addEventListener('click', function (event) {
      var target = event.target;
      if (!target || !target.closest) return;

      /* No preventDefault needed — these are <button type="button">, which submit nothing and navigate
         nowhere. That is the whole point: the address bar never changes. */
      var pill = target.closest('[data-et-cc-serie]');
      if (pill && root.contains(pill)) {
        var serie = pill.getAttribute('data-et-cc-serie');
        if (root.getAttribute('data-et-cc-active-serie') === serie) return;
        setSerie(root, serie);
        storeSerie(serie);
        return;
      }

      var head = target.closest('[data-et-cc-facet-toggle]');
      if (head && root.contains(head)) {
        var group = head.closest('.et-cc-facet');
        var open = !group.classList.contains('is-open');
        group.classList.toggle('is-open', open);
        head.setAttribute('aria-expanded', open ? 'true' : 'false');
        return;
      }

      var toggle = target.closest('[data-et-cc-instock]');
      if (toggle && root.contains(toggle)) {
        stateOf(root).inStock = toggle.getAttribute('aria-checked') !== 'true';
        applyFilters(root);
        return;
      }

      var reset = target.closest('[data-et-cc-reset]');
      if (reset && root.contains(reset)) {
        resetFilters(root);
      }
    });

    root.addEventListener('change', function (event) {
      var input = event.target.closest && event.target.closest('.et-cc-facet__input');
      if (!input || !root.contains(input)) return;

      var facet = input.getAttribute('data-et-cc-facet');
      var s = stateOf(root);
      var list = facet === 'serie' ? s.series
        : facet === 'color' ? s.colors
        : facet === 'handle' ? s.handles
        : facet === 'size' ? s.sizes
        : null;
      if (!list) return;

      toggleIn(list, input.value, input.checked);
      if (facet === 'serie') storeSerie(serieKey(s.series));
      applyFilters(root);
    });

    root.addEventListener('input', function (event) {
      var el = event.target;
      if (!el || !el.matches || !root.contains(el)) return;

      var s = stateOf(root);

      if (el.matches('[data-et-cc-price-min]')) {
        var min = clamp(parseInt(el.value, 10), s.priceFloor, s.priceMax);
        if (!isNaN(min)) { s.priceMin = min; applyFilters(root); }
      } else if (el.matches('[data-et-cc-price-max]')) {
        var max = clamp(parseInt(el.value, 10), s.priceMin, s.priceCeil);
        if (!isNaN(max)) { s.priceMax = max; applyFilters(root); }
      } else if (el.matches('[data-et-cc-price-range-min]')) {
        /* The two range inputs sit on top of each other, so each one is clamped against the other rather
           than allowed to cross it and invert the selection. */
        s.priceMin = Math.min(parseInt(el.value, 10), s.priceMax);
        applyFilters(root);
      } else if (el.matches('[data-et-cc-price-range-max]')) {
        s.priceMax = Math.max(parseInt(el.value, 10), s.priceMin);
        applyFilters(root);
      }
    });
  }

  // ----------------------------------------------------------------
  // Binding
  // ----------------------------------------------------------------

  function bind(root) {
    if (root.dataset.etCcInit === 'true') return;
    root.dataset.etCcInit = 'true';

    initFilters(root);

    /* One delegated listener on the section root, so a theme-editor re-render or any future markup swap inside
       the section keeps working without rebinding. */
    root.addEventListener('click', function (event) {
      var target = event.target;
      if (!target || !target.closest) return;

      var opener = target.closest('[data-et-cc-filters-open]');
      if (opener && root.contains(opener)) {
        event.preventDefault();
        openDrawer(root, opener);
        return;
      }

      var closer = target.closest('[data-et-cc-close]');
      if (closer && root.contains(closer)) {
        event.preventDefault();
        var drawer = closer.closest('[data-et-cc-drawer]');
        if (drawer) closeDrawer(drawer, true);
      }
    });

    root.addEventListener('keydown', function (event) {
      var drawer = root.querySelector('[data-et-cc-drawer].is-open');
      if (!drawer) return;

      if (event.key === 'Escape' || event.key === 'Esc') {
        event.preventDefault();
        closeDrawer(drawer, true);
        return;
      }

      if (event.key === 'Tab') trapTab(drawer, event);
    });

    revealSelectedPill(root);
  }

  function boot(scope) {
    var node = scope || document;
    /* shopify:section:load hands over the section wrapper itself, and querySelectorAll only walks descendants —
       so the scope node has to be tested as well. */
    if (node.matches && node.matches('.et-collection-grid')) bind(node);
    Array.prototype.forEach.call(node.querySelectorAll('.et-collection-grid'), bind);
  }

  if (document.readyState !== 'loading') {
    boot();
  } else {
    document.addEventListener('DOMContentLoaded', function () {
      boot();
    });
  }

  document.addEventListener('shopify:section:load', function (event) {
    boot(event.target);
  });

  /* Selecting the section in the theme editor re-runs the scroll so the merchant sees the selected pill. */
  document.addEventListener('shopify:section:select', function (event) {
    if (event.target && event.target.matches && event.target.matches('.et-collection-grid')) {
      revealSelectedPill(event.target);
    }
  });
})();
